import { definePreset } from "@primeuix/themes";
import Aura from "@primeuix/themes/aura";

import "primeicons/primeicons.css";
import Accordion from "primevue/accordion";
import AccordionTab from "primevue/accordiontab";
import Badge from "primevue/badge";
import Button from "primevue/button";
import ButtonGroup from "primevue/buttongroup";
import Card from "primevue/card";
import Checkbox from "primevue/checkbox";
import Column from "primevue/column";
import ColumnGroup from "primevue/columngroup";
import PrimeVue from "primevue/config";
import ConfirmDialog from "primevue/confirmdialog";
import ConfirmPopup from "primevue/confirmpopup";
import ContextMenu from "primevue/contextmenu";
import DataTable from "primevue/datatable";
import Dialog from "primevue/dialog";
import DialogService from "primevue/dialogservice";
import Divider from "primevue/divider";
import Drawer from "primevue/drawer";
import FileUpload from "primevue/fileupload";
import FloatLabel from "primevue/floatlabel";
import Fluid from "primevue/fluid";
import IconField from "primevue/iconfield";
import InputIcon from "primevue/inputicon";
import InputOtp from "primevue/inputotp";
import InputText from "primevue/inputtext";
import Listbox from "primevue/listbox";
import Menu from "primevue/menu";
import MenuBar from "primevue/menubar";
import Message from "primevue/message";
import Panel from "primevue/panel";
import Password from "primevue/password";
import Popover from "primevue/popover";
import ProgressBar from "primevue/progressbar";
import Ripple from "primevue/ripple";
import Select from "primevue/select";
import SelectButton from "primevue/selectbutton";
import Slider from "primevue/slider";
import SpeedDial from "primevue/speeddial";
import SplitButton from "primevue/splitbutton";
import Tab from "primevue/tab";
import TabList from "primevue/tablist";
import TabPanel from "primevue/tabpanel";
import TabPanels from "primevue/tabpanels";
import Tabs from "primevue/tabs";
import Tag from "primevue/tag";
import Textarea from "primevue/textarea";
import TieredMenu from "primevue/tieredmenu";
import ToastService from "primevue/toastservice";
import ToggleSwitch from "primevue/toggleswitch";
import Toolbar from "primevue/toolbar";
import Tooltip from "primevue/tooltip";
import TreeSelect from "primevue/treeselect";
import { App } from "vue";

import "@/style.scss";

import { ConfirmationService } from "./custom/services";

// css token customizations
const StekzAuraPreset = definePreset(Aura, {
    primitive: {
        borderRadius: 0,
        textColor: "palette-grey-400",
    },
    semantic: {
        // References --stekz-primary-* (see _variables.scss) instead of
        // hardcoded hex, so PrimeVue's own primary palette — Button, Tag,
        // focus rings, everything under --p-primary-* — tracks the active
        // rail mode (Design/Build/Run) the same way the app's own
        // --stekz-primary-color consumers do.
        primary: {
            50: "var(--stekz-primary-50)",
            100: "var(--stekz-primary-100)",
            200: "var(--stekz-primary-200)",
            300: "var(--stekz-primary-300)",
            400: "var(--stekz-primary-400)",
            500: "var(--stekz-primary-500)",
            600: "var(--stekz-primary-600)",
            700: "var(--stekz-primary-700)",
            800: "var(--stekz-primary-800)",
            900: "var(--stekz-primary-900)",
            950: "var(--stekz-primary-950)",
        },
        // Secondary aliases --stekz-build-color (the Gotoko brand orange —
        // see _variables.scss's comment above --stekz-primary-color), and
        // tertiary --stekz-tertiary-color (my.stekz's own canonical green,
        // deliberately not the marketing DS 2.0 value) — both ramped at the
        // same color-mix steps as the primary ramp above.
        secondary: {
            50: "color-mix(in srgb, var(--stekz-build-color), white 92%)",
            100: "color-mix(in srgb, var(--stekz-build-color), white 80%)",
            200: "color-mix(in srgb, var(--stekz-build-color), white 50%)",
            300: "color-mix(in srgb, var(--stekz-build-color), white 25%)",
            400: "var(--stekz-build-color)",
            500: "color-mix(in srgb, var(--stekz-build-color), black 25%)",
            600: "color-mix(in srgb, var(--stekz-build-color), black 50%)",
            700: "color-mix(in srgb, var(--stekz-build-color), black 65%)",
            800: "color-mix(in srgb, var(--stekz-build-color), black 80%)",
            900: "color-mix(in srgb, var(--stekz-build-color), black 90%)",
            950: "color-mix(in srgb, var(--stekz-build-color), black 95%)",
        },
        tertiary: {
            50: "color-mix(in srgb, var(--stekz-tertiary-color), white 92%)",
            100: "color-mix(in srgb, var(--stekz-tertiary-color), white 80%)",
            200: "color-mix(in srgb, var(--stekz-tertiary-color), white 50%)",
            300: "color-mix(in srgb, var(--stekz-tertiary-color), white 25%)",
            400: "var(--stekz-tertiary-color)",
            500: "color-mix(in srgb, var(--stekz-tertiary-color), black 25%)",
            600: "color-mix(in srgb, var(--stekz-tertiary-color), black 50%)",
            700: "color-mix(in srgb, var(--stekz-tertiary-color), black 65%)",
            800: "color-mix(in srgb, var(--stekz-tertiary-color), black 80%)",
            900: "color-mix(in srgb, var(--stekz-tertiary-color), black 90%)",
            950: "color-mix(in srgb, var(--stekz-tertiary-color), black 95%)",
        },
    },
});
export function initPrimeVue(app: App<Element>) {
    app.use(PrimeVue, {
        // Default theme configuration
        ripple: true,
        theme: {
            preset: StekzAuraPreset,
            options: {
                prefix: "p",
                darkModeSelector: ".dark-mode",
                cssLayer: false, // Todo see https://v4.primevue.org/theming/styled/#csslayer
            },
        },
    });

    app.use(ConfirmationService);
    // https://primevue.org/dynamicdialog/
    app.use(DialogService);
    app.use(ToastService);

    app.component("Accordion", Accordion);
    app.component("AccordionTab", AccordionTab);
    app.component("Badge", Badge);
    app.component("Button", Button);
    app.component("ButtonGroup", ButtonGroup);
    app.component("Card", Card);
    app.component("ConfirmDialog", ConfirmDialog);
    app.component("ConfirmPopup", ConfirmPopup);
    app.component("ContextMenu", ContextMenu);
    app.component("Dialog", Dialog);
    app.component("Menu", Menu);
    app.component("MenuBar", MenuBar);
    app.component("TieredMenu", TieredMenu);
    app.component("Select", Select);
    app.component("FileUpload", FileUpload);
    app.component("SplitButton", SplitButton);
    app.component("Toolbar", Toolbar);
    app.component("IconField", IconField);
    app.component("InputIcon", InputIcon);
    app.component("InputText", InputText);
    app.component("InputOtp", InputOtp);
    app.component("Message", Message);
    app.component("DataTable", DataTable);
    app.component("Column", Column);
    app.component("Panel", Panel);
    app.component("Textarea", Textarea);
    app.component("Tag", Tag);
    app.component("Drawer", Drawer);
    app.component("Listbox", Listbox);
    app.component("ColumnGroup", ColumnGroup);
    app.component("Tabs", Tabs);
    app.component("TabList", TabList);
    app.component("Tab", Tab);
    app.component("TabPanels", TabPanels);
    app.component("TabPanel", TabPanel);
    app.component("Checkbox", Checkbox);
    app.component("SelectButton", SelectButton);
    app.component("SpeedDial", SpeedDial);
    app.component("Slider", Slider);
    app.component("ToggleSwitch", ToggleSwitch);
    app.component("Password", Password);
    app.component("ProgressBar", ProgressBar);
    app.component("TreeSelect", TreeSelect);
    app.component("Divider", Divider);
    app.component("FloatLabel", FloatLabel);
    app.component("Fluid", Fluid);
    app.component("Popover", Popover);

    // PrimeVue Directives
    app.directive("ripple", Ripple);
    app.directive("tooltip", Tooltip);
}

export const baseSuccessToastMessage = {
    severity: "success" as const,
    life: 5000,
    closable: true,
};
